import {
	type CurrencyEnum,
	type PreviewOrderQuoteOfferFeasibilityInput,
	OrderShippingMethod,
} from '@codezo/graphql';
import { Clock } from 'lucide-react';
import { useMemo } from 'react';
import { CurrencyInput } from '../../ui/currency-input';
import { NumberInput } from '../../ui/number-input';
import { cn, formatArrivalWindow } from '../../../lib/utils';
import { FeasibilityBadge } from '../../ui/feasibility-badge';
import { SHIPPING_METHOD_LABEL } from '../../../lib/status-configs';
import {
	type FeasibilityDelivery,
	useFeasibilityPreview,
} from '../../../hooks/useFeasibilityPreview';
import {
	type DeliveryDraft,
	deliveryIncompleteReason,
	filled,
	isDeliveryDaysUnknown,
	isDeliveryIncomplete,
} from './delivery-rules';
import { SplitsSection } from './SplitsSection';
import type { SplitDraft } from './split-rules';

/** Both methods are always shown; the admin fills in whichever apply. */
export const METHODS = [OrderShippingMethod.Standard, OrderShippingMethod.Express];

const int = (v: string) => {
	const n = parseInt(v, 10);
	return Number.isFinite(n) ? n : 0;
};

/**
 * One quantity tier: its two methods, and any splits offered on top of them.
 *
 * A component rather than a branch of the modal because the arrival dates are
 * per tier — production time is a property of the tier, so every one of them
 * needs its own feasibility preview, and a hook cannot be called in a loop. That
 * is also what lets the tiers stack: they used to sit behind tabs, which hid the
 * gaps the editor exists to show and made "price every quantity" a thing you had
 * to remember rather than see.
 */
export function TierDeliverySection({
	quantity,
	pricing,
	slots,
	splits,
	currency,
	deadline,
	acceptDeadline,
	onPatchSlot,
	onChangeSplits,
}: {
	quantity: number;
	pricing: { id: string; minProductionDays?: number | null; maxProductionDays?: number | null };
	/** The two method drafts, by method. */
	slots: Record<string, DeliveryDraft>;
	splits: SplitDraft[];
	currency: CurrencyEnum;
	deadline: string | null;
	acceptDeadline: string | null;
	onPatchSlot: (method: OrderShippingMethod, patch: Partial<DeliveryDraft>) => void;
	onChangeSplits: (next: SplitDraft[]) => void;
}) {
	/* Built from the days currently typed, not the saved ones, so each arrival
	   tracks what the admin is doing right now. Splits are deliberately absent:
	   the result comes back keyed by method and a split's method is derived, so
	   previewing one would overwrite the real method's entry. A leg travels at
	   its method's speed, so the method's own arrival is the leg's. */
	const feasInput = useMemo((): PreviewOrderQuoteOfferFeasibilityInput | null => {
		if (pricing.minProductionDays == null) return null;
		return {
			minProductionDays: pricing.minProductionDays,
			maxProductionDays: pricing.maxProductionDays ?? null,
			deadline: deadline ?? undefined,
			acceptDeadline: acceptDeadline ?? undefined,
			deliveries: METHODS.flatMap((method) => {
				const slot = slots[method];
				if (!slot || !filled(slot.deliveryDaysMin) || !filled(slot.deliveryDaysMax)) {
					return [];
				}
				return [
					{
						shippingMethod: method,
						deliveryDaysMin: int(slot.deliveryDaysMin),
						deliveryDaysMax: int(slot.deliveryDaysMax),
					},
				];
			}),
		};
	}, [pricing, deadline, acceptDeadline, slots]);

	const { production, byMethod, loading: previewLoading } = useFeasibilityPreview(feasInput);

	const readyWindow = formatArrivalWindow(
		production?.projectedCompletion?.ifAcceptedNow?.earliest,
		production?.projectedCompletion?.ifAcceptedNow?.latest
	);

	/* The whole verdict, not a formatted date. A method that misses the deadline
	   has to say so — in the colour, the copy and the breakdown — and a string
	   can only carry the date. */
	const resultFor = (method: OrderShippingMethod) => byMethod.get(method) ?? null;

	const productionReady = pricing.minProductionDays != null;
	const tierDays = {
		productionMin: pricing.minProductionDays,
		productionMax: pricing.maxProductionDays,
	};

	return (
		<section className="flex flex-col gap-2.5">
			{/* The quantity leads, readiness sits at the far edge. They are the two
			    ends of the same statement — how many, and when they exist — and
			    pushing the date right lines it up down the stack of tiers, so the
			    dates can be read against each other without the quantities in the
			    way. */}
			<div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
				<h3 className="font-mono text-base font-medium text-text-1">
					{quantity.toLocaleString()} pcs
				</h3>
				<span className="text-xs text-text-4">
					{readyWindow
						? `Production ready ${readyWindow}`
						: 'Production time not set on this tier'}
				</span>
			</div>

			{/* Side by side: the two are alternatives on the same quantity, and the
			    only way to price one against the other is to see both at once. */}
			<div className="grid gap-2.5 sm:grid-cols-2">
				{METHODS.map((method) => (
					<MethodCard
						key={method}
						method={method}
						slot={slots[method]}
						currency={currency}
						deadline={deadline}
						productionReady={productionReady}
						tierDays={tierDays}
						result={resultFor(method)}
						previewLoading={previewLoading}
						onPatch={(patch) => onPatchSlot(method, patch)}
					/>
				))}
			</div>

			<SplitsSection
				splits={splits}
				tierQuantity={quantity}
				currency={currency}
				deadline={deadline}
				acceptDeadline={acceptDeadline}
				production={{ min: pricing.minProductionDays, max: pricing.maxProductionDays }}
				onChange={onChangeSplits}
			/>
		</section>
	);
}

/**
 * One method on one quantity: what it costs, and how long it takes.
 *
 * The lead time reads as a sentence — "5 to 7 days" — rather than as two
 * labelled columns. It is one fact with two ends, and labelling each end
 * separately spent the width twice and invited half of it being filled in.
 */
function MethodCard({
	method,
	slot,
	currency,
	deadline,
	productionReady,
	tierDays,
	result,
	previewLoading,
	onPatch,
}: {
	method: OrderShippingMethod;
	slot: DeliveryDraft | undefined;
	currency: CurrencyEnum;
	deadline: string | null;
	/** Nothing can be projected without it — the tier has no production time. */
	productionReady: boolean;
	/** The tier's production window, so the hover states the durations even on a
	 * quote with no deadline, where the server sends no breakdown at all. */
	tierDays: { productionMin?: number | null; productionMax?: number | null };
	result: FeasibilityDelivery | null;
	previewLoading: boolean;
	onPatch: (patch: Partial<DeliveryDraft>) => void;
}) {
	const draft = slot ?? { price: '', deliveryDaysMin: '', deliveryDaysMax: '' };
	const incomplete = isDeliveryIncomplete(draft);
	const daysComplete = filled(draft.deliveryDaysMin) && filled(draft.deliveryDaysMax);

	return (
		<div
			className={cn(
				'flex flex-col rounded-lg border',
				incomplete ? 'border-status-warn' : 'border-line-2'
			)}
		>
			<div className="flex items-center gap-2 px-3 pt-2.5 pb-2">
				<span
					aria-hidden
					className={cn(
						'size-1.5 shrink-0 rounded-[1px]',
						method === OrderShippingMethod.Express ? 'bg-[var(--accent)]' : 'bg-text-3'
					)}
				/>
				<span className="text-xs font-medium text-text-1">
					{SHIPPING_METHOD_LABEL[method]}
				</span>
			</div>

			<div className="flex flex-wrap items-end gap-x-4 gap-y-2 px-3 pb-2.5">
				<div className="flex min-w-24 flex-1 flex-col gap-1">
					<span className="text-2xs text-text-4">Price</span>
					<CurrencyInput
						currency={currency}
						value={draft.price}
						onChange={(e) => onPatch({ price: (e.target as HTMLInputElement).value })}
						placeholder="0.00"
						aria-label={`${SHIPPING_METHOD_LABEL[method]} shipping price`}
						min={0}
						step="any"
					/>
				</div>
				{/* The day fields are w-20, not the w-14 this started at. A
				    NumberInput spends 24px on its steppers and 20px on padding before
				    a character is drawn, so a 56px field left ~11px of text — one
				    digit — and a two-digit lead time typed into it was invisible. At
				    80px there is room for four. The cost is that Days wraps onto
				    its own line inside the card at this width, which the flex-wrap
				    above was already there to handle. */}
				<div className="flex flex-col gap-1">
					<span className="text-2xs text-text-4">Days</span>
					<div className="flex items-center gap-1.5">
						<div className="w-20">
							<NumberInput
								value={draft.deliveryDaysMin}
								onChange={(e) =>
									onPatch({
										deliveryDaysMin: (e.target as HTMLInputElement).value,
									})
								}
								placeholder="—"
								aria-label={`${SHIPPING_METHOD_LABEL[method]} minimum transit days`}
								min={0}
							/>
						</div>
						<span className="text-xs text-text-4">to</span>
						<div className="w-20">
							<NumberInput
								value={draft.deliveryDaysMax}
								onChange={(e) =>
									onPatch({
										deliveryDaysMax: (e.target as HTMLInputElement).value,
									})
								}
								placeholder="—"
								aria-label={`${SHIPPING_METHOD_LABEL[method]} maximum transit days`}
								min={0}
							/>
						</div>
					</div>
				</div>
			</div>

			{/* The consequence of the two fields above it, in the card's own footer
			    rather than floating under them: it is an output, not another input. */}
			{/* The verdict, not just the date — but the PILL, not the panel.
			 *
			 * `FeasibilityBadge` colours on the verdict and carries the whole
			 * breakdown in its hover. `FeasibilityFeedback` would print those facts
			 * inline instead, which in a two-up card grid means the same capacity,
			 * lead time and overshoot rendered twice side by side and a card that
			 * quadruples in height the moment a deadline slips. The reasoning is one
			 * hover away, and the colour is what actually needs to be glanceable. */}
			<div className="mt-auto border-t border-line-1 px-3 py-1.5 text-xs">
				{incomplete ? (
					<span className="text-status-warn">{deliveryIncompleteReason(draft)}</span>
				) : !daysComplete ? (
					<span className="text-text-4">
						{isDeliveryDaysUnknown(draft)
							? 'Priced — add the transit days before this can be sent'
							: 'Leave blank to skip this method'}
					</span>
				) : !productionReady ? (
					/* Days are set but the tier has no production time, so there is no
					   date to project from — said here rather than left blank, since a
					   silent footer reads as "no arrival" instead of "not yet known". */
					<span className="flex items-center gap-1.5 text-text-4">
						<Clock size={11} className="shrink-0" />
						Set production days to preview arrival
					</span>
				) : result ? (
					<FeasibilityBadge
						verdict={result.verdict}
						projected={result.projectedDelivery}
						deadline={deadline}
						breakdown={result.breakdown}
						days={{
							...tierDays,
							transitMin: Number(draft.deliveryDaysMin),
							transitMax: Number(draft.deliveryDaysMax),
						}}
					/>
				) : previewLoading ? (
					<span className="text-text-4">Checking arrival…</span>
				) : null}
			</div>
		</div>
	);
}
